import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  createRecurringTemplate,
  updateRecurringTemplate,
} from '@/lib/firestore-recurring';
import { RecurringTransactionsList } from '@/components/RecurringTransactionsList';
import { RecurringTransactionForm } from '@/components/RecurringTransactionForm';
import type { RecurringTemplate, RecurringTemplateInput } from '@/types/recurring';
import { ArrowLeft, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

export function RecurringTransactions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RecurringTemplate | null>(
    null
  );
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = (template: RecurringTemplate) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleSubmit = async (data: RecurringTemplateInput) => {
    if (!user) {
      alert('User not authenticated. Please log in again.');
      return;
    }

    console.log('💾 Submitting recurring template:', data);

    try {
      if (editingTemplate) {
        console.log('✏️ Updating template:', editingTemplate.id);
        await updateRecurringTemplate(editingTemplate.id, data);
        console.log('✅ Template updated successfully');
      } else {
        console.log('➕ Creating new template');
        const templateId = await createRecurringTemplate(user.uid, data);
        console.log('✅ Template created with ID:', templateId);
      }

      setShowForm(false);
      setEditingTemplate(null);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('❌ Error saving recurring template:', error);

      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          alert('Permission denied. Please check Firebase rules.');
        } else if (error.message.includes('index')) {
          alert('Database index required. Check console for link to create it.');
        } else if (error.message.includes('undefined')) {
          alert('Invalid data. Please fill all required fields.');
        } else {
          alert(`Failed to save: ${error.message}`);
        }
      } else {
        alert('Failed to save. Check console for details.');
      }

      throw error;
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="shrink-0 rounded-lg p-2 text-slate-600 transition-all hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                <ArrowLeft size={22} />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold text-slate-900 dark:text-slate-100 sm:text-2xl">
                  {t('recurring.title')}
                </h1>
                <p className="hidden text-sm text-slate-600 dark:text-slate-400 sm:block">
                  {t('recurring.subtitle')}
                </p>
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-3 py-2 font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 sm:px-4"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">{t('recurring.createRecurring')}</span>
              <span className="sm:hidden text-sm">{t('common.add') || 'Add'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* Info Card */}
        <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50/80 p-4 backdrop-blur-xl dark:border-blue-900 dark:bg-blue-900/20 sm:mb-8 sm:p-6">
          <h3 className="mb-3 text-base font-bold text-blue-900 dark:text-blue-100 sm:text-lg">
            {t('recurring.infoTitle')}
          </h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <li>
              • <strong>{t('recurring.infoAutomatic').split(':')[0]}:</strong>{' '}
              {t('recurring.infoAutomatic').split(':').slice(1).join(':').trim()}
            </li>
            <li>
              • <strong>{t('recurring.infoPause').split(':')[0]}:</strong>{' '}
              {t('recurring.infoPause').split(':').slice(1).join(':').trim()}
            </li>
            <li>
              • <strong>{t('recurring.infoEdit').split(':')[0]}:</strong>{' '}
              {t('recurring.infoEdit').split(':').slice(1).join(':').trim()}
            </li>
            <li>
              • <strong>{t('recurring.infoEndDate').split(':')[0]}:</strong>{' '}
              {t('recurring.infoEndDate').split(':').slice(1).join(':').trim()}
            </li>
          </ul>
        </div>

        {/* Recurring Templates List */}
        <RecurringTransactionsList
          key={refreshKey}
          onEdit={handleEdit}
          onRefresh={handleRefresh}
        />
      </main>

      {/* Form Modal */}
      {showForm && (
        <RecurringTransactionForm
          template={editingTemplate}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}
